/**
 * ScenarioLibraryModal — full library of saved scenarios with plain-text search
 * by name + inline name/description editing.
 *
 * Used by the Simulator tab's "Load from Library" button at the top of the page.
 * Visually mirrors the Roadmap tab's scenario picker (grid of ScenarioCard).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, message, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { ScenarioCard } from './ScenarioCard';
import { scenarioService } from '../services/scenarioService';
import type { PricingPayload, SavedScenario } from '../types';

interface Props {
  open: boolean;
  pricing: PricingPayload;
  scenarios: SavedScenario[];
  /** Id of the currently-loaded scenario in the Simulator, for highlighting. */
  loadedScenarioId: string | null;
  onClose: () => void;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  /** Called whenever the library mutates a scenario (rename / re-describe) so
      the parent can refresh its own copy. */
  onScenarioUpdated: (updated: SavedScenario) => void;
}

export function ScenarioLibraryModal({
  open, pricing, scenarios, loadedScenarioId,
  onClose, onLoad, onDelete, onScenarioUpdated,
}: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  /* Inline edit mode — one scenario at a time. Stores the in-progress name +
     description so the user can cancel without persisting. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setEditingId(null);
    }
  }, [open]);

  // Plain-text search by name (case-insensitive). Description is searchable too
  // since both fields are user-authored and small.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scenarios;
    return scenarios.filter(s =>
      s.name.toLowerCase().includes(q)
      || (s.description ?? '').toLowerCase().includes(q));
  }, [query, scenarios]);

  function startEdit(s: SavedScenario) {
    setEditingId(s.scenario_id);
    setEditName(s.name);
    setEditDesc(s.description ?? '');
  }

  async function commitEdit(s: SavedScenario) {
    const newName = editName.trim();
    if (!newName) {
      message.warning(t('costSimulator.scenarios.lib.nameRequired'));
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await scenarioService.updateMeta(
        s.scenario_id, newName, editDesc.trim() ? editDesc : null,
      );
      onScenarioUpdated(updated);
      setEditingId(null);
      message.success(t('costSimulator.scenarios.updated'));
    } catch {
      message.error(t('common.error'));
    } finally {
      setSavingEdit(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('costSimulator.scenarios.lib.title')}
      footer={null}
      width={820}
      destroyOnClose
    >
      {/* Search bar */}
      <Input dir="auto"
        prefix={<SearchOutlined style={{ opacity: 0.5 }} />}
        placeholder={t('costSimulator.scenarios.lib.searchPlaceholder')}
        value={query}
        onChange={e => setQuery(e.target.value)}
        allowClear
        style={{ marginBlockEnd: 14 }}
        autoFocus
      />

      {scenarios.length === 0 ? (
        <Empty description={t('costSimulator.scenarios.empty')} />
      ) : filtered.length === 0 ? (
        <Empty description={t('costSimulator.scenarios.lib.noMatches', { query })} />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12, maxHeight: 520, overflowY: 'auto', paddingInlineEnd: 4,
        }}>
          {filtered.map(s => editingId === s.scenario_id ? (
            // ── Inline edit view ───────────────────────────────────────────
            <div key={s.scenario_id} style={{
              padding: 12,
              background: token.colorBgContainer,
              border: `1px solid ${token.colorPrimary}`,
              borderRadius: token.borderRadiusLG,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <Input dir="auto"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                placeholder={t('costSimulator.scenarios.lib.namePlaceholder')}
                size="small"
                style={{ fontWeight: 600 }}
              />
              <Input.TextArea dir="auto"
                value={editDesc}
                onChange={e => setEditDesc(e.target.value)}
                placeholder={t('costSimulator.scenarios.lib.descriptionPlaceholder')}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setEditingId(null)}>
                  {t('common.cancel')}
                </Button>
                <Button size="small" type="primary" loading={savingEdit} onClick={() => commitEdit(s)}>
                  {t('common.save')}
                </Button>
              </div>
            </div>
          ) : (
            // ── Read view ───────────────────────────────────────────────────
            //
            // Edit button is rendered as a CHILD of ScenarioCard's action row
            // (via the onEdit prop). Top-corner absolute placement collided
            // with the price tag, so it now joins Load / Delete / Duplicate.
            <ScenarioCard
              key={s.scenario_id}
              scenario={s}
              pricing={pricing}
              isLoaded={loadedScenarioId === s.scenario_id}
              onLoad={() => { onLoad(s.scenario_id); onClose(); }}
              onDelete={() => onDelete(s.scenario_id)}
              onEdit={() => startEdit(s)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
